import { type ProblemDetails, isProblemDetails, traceReference } from '@lions/api-client';

/**
 * Turning API failures into what the admin sees (02-technical-architecture.md § Error handling and
 * logging policy, Clients): field errors on the form for 400 VALIDATION_FAILED, a toast with the
 * problem detail for other 4xx, and "Something went wrong. Reference: <traceId[0..8]>" for 5xx.
 */

/** A failed API call that answered with an RFC 9457 problem body. */
export class ApiError extends Error {
  override readonly name = 'ApiError';
  constructor(readonly problem: ProblemDetails) {
    super(problem.detail || problem.title);
  }
  get status(): number {
    return this.problem.status;
  }
}

/** A failed HTTP call without a problem body (a proxy page, an empty 502, a storage PUT). */
export class HttpError extends Error {
  override readonly name = 'HttpError';
  constructor(
    readonly status: number,
    message = `The request failed (HTTP ${status}).`,
  ) {
    super(message);
  }
}

export interface FieldError {
  path: string;
  message: string;
}

export interface UiError {
  /** Text for the toast (or the inline alert on the login screens). */
  message: string;
  severity: 'error' | 'warning';
  /** 400 VALIDATION_FAILED only: one entry per `errors[]` item, to set on the form. */
  fieldErrors: FieldError[];
  /** HTTP status, or null when the request never got an answer. */
  status: number | null;
}

export const GENERIC_ERROR_MESSAGE = 'Something went wrong.';
export const NETWORK_ERROR_MESSAGE =
  'The server could not be reached. Check your connection and try again.';

/** Builds the typed error for a failed response body (problem+json or anything else). */
export function errorFromResponse(status: number, body: unknown): ApiError | HttpError {
  return isProblemDetails(body) ? new ApiError(body) : new HttpError(status);
}

function formatWait(seconds: number): string {
  if (seconds < 60) return `${seconds} second${seconds === 1 ? '' : 's'}`;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

function serverError(problem: ProblemDetails | null): string {
  return problem?.traceId
    ? `${GENERIC_ERROR_MESSAGE} Reference: ${traceReference(problem)}`
    : GENERIC_ERROR_MESSAGE;
}

function fromProblem(problem: ProblemDetails): UiError {
  const base = { severity: 'error' as const, fieldErrors: [], status: problem.status };
  if (problem.status >= 500) return { ...base, message: serverError(problem) };

  const detail = problem.detail || problem.title;
  switch (problem.code) {
    case 'VALIDATION_FAILED':
      return {
        ...base,
        message: detail,
        fieldErrors: (problem.errors ?? []).map(({ path, message }) => ({ path, message })),
      };
    case 'RATE_LIMITED':
      return {
        ...base,
        severity: 'warning',
        message:
          problem.retryAfter === undefined
            ? detail
            : `${detail} Try again in ${formatWait(problem.retryAfter)}.`,
      };
    case 'IN_USE': {
      const meta = problem.meta;
      const references = meta && 'references' in meta ? meta.references : [];
      const usedBy = references.map(({ entity, count }) => `${entity} (${count})`).join(', ');
      return { ...base, message: usedBy ? `${detail} Used by: ${usedBy}.` : detail };
    }
    default:
      return { ...base, message: detail };
  }
}

/** Maps any thrown value (ApiError, HttpError, a problem body, a network failure) to a UiError. */
export function toUiError(error: unknown): UiError {
  if (error instanceof ApiError) return fromProblem(error.problem);
  if (isProblemDetails(error)) return fromProblem(error);
  if (error instanceof HttpError) {
    return {
      message: error.status >= 500 ? GENERIC_ERROR_MESSAGE : error.message,
      severity: 'error',
      fieldErrors: [],
      status: error.status,
    };
  }
  // fetch rejects with a TypeError when the server cannot be reached (DNS, CORS, offline).
  if (error instanceof TypeError) {
    return { message: NETWORK_ERROR_MESSAGE, severity: 'error', fieldErrors: [], status: null };
  }
  return { message: GENERIC_ERROR_MESSAGE, severity: 'error', fieldErrors: [], status: null };
}

/** The HTTP status of a thrown API error, if it has one. */
export function errorStatus(error: unknown): number | null {
  if (error instanceof ApiError || error instanceof HttpError) return error.status;
  if (isProblemDetails(error)) return error.status;
  return null;
}
